import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';
import { notifyError } from '@/lib/notify';
import { apiErrorText } from '../../../../utils';

/**
 * Accepting (prebinding) a quote, for every product. The dialog can't be closed while the prebind is
 * in flight, so a refusal is always shown, with the server's message, in the dialog that sent it.
 * Once the prebind succeeds the dialog closes; a failure refreshing the quote after that is reported
 * on its own, since the quote is prebound either way. Each opening starts without a message.
 */
export function AcceptQuoteDialog({
  open,
  onClose,
  onAccept,
  onAccepted,
}: {
  open: boolean;
  onClose: () => void;
  onAccept: () => Promise<unknown>;
  onAccepted?: () => unknown;
}) {
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setError(null);
    setSubmitting(false);
  }, [open]);

  const close = () => {
    if (!submitting) onClose();
  };

  const handleConfirm = async () => {
    if (!open || submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      await onAccept();
    } catch (err) {
      setError(apiErrorText(err, 'Accepting the quote failed'));
      setSubmitting(false);
      return;
    }
    onClose();
    if (onAccepted) {
      Promise.resolve()
        .then(() => onAccepted())
        .catch(() => notifyError('The quote was accepted, but refreshing it failed. Reload the page to see it.'));
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Accept Quote</DialogTitle>
          <DialogDescription>Please confirm that you and your client are accepting the quote and ready to bind.</DialogDescription>
        </DialogHeader>
        {error ? <p className="text-sm text-destructive">{error}</p> : null}
        <DialogFooter>
          <Button variant="outline" onClick={close} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={submitting}>
            {submitting ? <Spinner /> : null}
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
